import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNav } from '../../store/nav';
import { haptic } from '../../hooks/useTelegram';
import { useBackHandler } from '../../hooks/useBackHandler';
import { useActiveWallet } from '../../store/wallets';
import { useBalances } from '../../hooks/useChain';
import { useToast } from '../../store/toast';
import { useQrPay, useQrPaymentStatus } from '../../hooks/useQrPay';
import { qrLinkInfoApi, qrQuoteApi, isFinal, type QrQuote, type QrPayment } from '../../lib/qr';
import { CoinIcon } from '../CoinIcon';
import { FigIcon } from '../FigIcon';
import { ReauthGate } from './ReauthGate';

const CONFETTI_COLORS = ['#2195f1', '#58b272', '#f7b32b', '#ec3240', '#8ccdff', '#8458db'];

type Step = 'scan' | 'amount' | 'estimate' | 'auth' | 'paying' | 'settling' | 'success' | 'failed';

const usdt = (n: number) => `${n.toFixed(2)} USDT`;

/** Real Telegram client with a working native QR sheet — not the browser shim. */
function canNativeScan(): boolean {
  const tg = window.Telegram?.WebApp;
  return !!tg?.showScanQrPopup && !!tg.platform && tg.platform !== 'unknown';
}

/**
 * Paying an Asian QR code in USDT.
 *
 * The code is read by Telegram's camera, priced by the provider, and paid with
 * an ordinary USDT transfer to the treasury. The store itself is settled by the
 * provider afterwards, so the last screen waits on that result rather than
 * claiming success the moment the money leaves — a payment can still fail after
 * the transfer confirms, and when it does the refund is owed and said so.
 */
export function QrPayScreen() {
  const { t } = useTranslation();
  const pop = useNav((s) => s.pop);
  const showToast = useToast((s) => s.show);
  const wallet = useActiveWallet();
  const balances = useBalances(wallet?.address).data;
  const { pay } = useQrPay();

  const [step, setStep] = useState<Step>('scan');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState('');
  const [amount, setAmount] = useState('');
  const [store, setStore] = useState<{ name: string | null; currency: string } | null>(null);
  const [quote, setQuote] = useState<QrQuote | null>(null);
  const [payment, setPayment] = useState<QrPayment | null>(null);
  const autoScanRef = useRef(false);

  // Once the payment is open, follow it until the provider stops moving it.
  const settling = step === 'settling' ? payment : null;
  const { data: live } = useQrPaymentStatus(settling?.id ?? null);

  useEffect(() => {
    if (!live || step !== 'settling') return;
    if (!isFinal(live.status)) return;
    setPayment(live);
    haptic('heavy');
    setStep(live.status === 'completed' ? 'success' : 'failed');
  }, [live, step]);

  // Native Telegram back: step back through the flow rather than leaving it.
  useBackHandler(step === 'amount', () => setStep('scan'));
  useBackHandler(step === 'estimate', () => setStep(store ? 'amount' : 'scan'));
  useBackHandler(step === 'success' || step === 'failed', () => pop());

  const balance = balances?.usdt ?? 0;
  const short = quote != null && balance < quote.totalUsdt;

  const mapError = (e: unknown): string => {
    const code = e instanceof Error ? e.message : String(e);
    const map: Record<string, string> = {
      qr_unavailable: t('qrPay.err.unavailable'),
      qr_paused: t('qrPay.err.unavailable'),
      qr_offline: t('qrPay.err.offline'),
      invalid_qr: t('qrPay.err.invalidQr'),
      amount_over_limit: t('qrPay.err.overLimit'),
      unsupported_currency: t('qrPay.err.unsupportedCurrency'),
      invalid_amount: t('qrPay.err.invalidAmount'),
      insufficient_balance: t('qrPay.err.insufficient'),
      payment_not_confirmed: t('qrPay.err.notConfirmed'),
      transfer_in_progress: t('send.err.inProgress'),
      transfer_preparing: t('send.err.preparing'),
      rpc_unavailable: t('send.err.network'),
      timeout: t('send.err.timeout'),
      ws_timeout: t('send.err.timeout'),
      ws_closed: t('send.err.timeout'),
      signed_transaction_expired: t('send.err.signedExpired'),
    };
    return map[code] ?? t('qrPay.err.generic');
  };

  /** Read a scanned payload: what the store is, and whether it names a price. */
  const read = async (payload: string) => {
    setBusy(true);
    setNote('');
    try {
      const info = await qrLinkInfoApi(payload);
      setLink(payload);
      setStore({ name: info.merchantName, currency: info.fiatCurrency });
      if (info.amountRequired) {
        setAmount('');
        setStep('amount');
        return;
      }
      setQuote(await qrQuoteApi(payload));
      setStep('estimate');
    } catch (e) {
      haptic();
      showToast(mapError(e));
    } finally {
      setBusy(false);
    }
  };

  const scan = () => {
    haptic();
    const tg = window.Telegram?.WebApp;
    if (!canNativeScan() || !tg?.showScanQrPopup) {
      setNote(t('qrPay.cameraUnavailable'));
      return;
    }
    tg.showScanQrPopup({ text: t('qrPay.scanHint') }, (text: string) => {
      tg.closeScanQrPopup?.();
      void read(text);
      return true; // close the scanner
    });
  };

  // The dark square is decoration — open Telegram's camera as soon as we land,
  // so the user isn't stuck staring at a fake viewfinder with the CTA below the fold.
  useEffect(() => {
    if (step !== 'scan' || autoScanRef.current || !canNativeScan()) return;
    autoScanRef.current = true;
    const id = window.setTimeout(scan, 250);
    return () => clearTimeout(id);
    // Intentionally once per visit of the scan step.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  /** Price a code that made the user name the amount. */
  const priceTypedAmount = async () => {
    const n = Number(amount);
    if (!Number.isFinite(n) || n <= 0) return;
    setBusy(true);
    try {
      setQuote(await qrQuoteApi(link, amount));
      setStep('estimate');
    } catch (e) {
      haptic();
      showToast(mapError(e));
    } finally {
      setBusy(false);
    }
  };

  // Reauth succeeded → pay. Returns true so the gate never shows a wrong-PIN
  // state for a failure that happened after the PIN was accepted.
  const runPay = async (reauthToken?: string): Promise<boolean> => {
    if (!quote || !wallet) return true;
    setStep('paying');
    try {
      const result = await pay({
        quote,
        ...(store && amount ? { amount } : {}),
        walletId: wallet.id,
        index: wallet.derivationIndex ?? 0,
        address: wallet.address,
        reauthToken,
      });
      setPayment(result);
      if (isFinal(result.status)) {
        haptic('heavy');
        setStep(result.status === 'completed' ? 'success' : 'failed');
      } else {
        setStep('settling');
      }
    } catch (e) {
      haptic();
      console.error('[qr] payment failed', e);
      showToast(mapError(e));
      setStep('estimate');
    }
    return true;
  };

  // ── Success ──────────────────────────────────────────────────────────────
  if (step === 'success' && payment) {
    return (
      <SuccessView
        total={usdt(payment.totalUsdt)}
        merchant={payment.merchantName ?? t('qrPay.theStore')}
        onDone={pop}
      />
    );
  }

  // ── Failed — the money is ours and the store was not paid ────────────────
  if (step === 'failed' && payment) {
    const why = payment.storeBlacklisted
      ? t('qrPay.failed.blacklisted')
      : payment.status === 'expired'
        ? t('qrPay.failed.expired')
        : t('qrPay.failed.generic');
    return (
      <div className="push-enter flex min-h-screen flex-col bg-surface px-8 pb-8 pt-4">
        <div className="flex flex-1 flex-col items-center justify-center gap-5 text-center">
          {/* The flow's receipt mark, in the decline colour — there is no cross in
              the icon set, and inventing one here would break the Figma source. */}
          <span className="grid size-[72px] place-items-center rounded-full bg-negative text-white">
            <FigIcon name="pfReceipt" size={34} />
          </span>
          <h1 className="text-[24px] font-medium text-ink">{t('qrPay.failed.title')}</h1>
          <p className="max-w-[300px] text-[15px] leading-[1.44] text-muted">{why}</p>
          <p className="max-w-[300px] text-[14px] leading-[1.44] text-muted">
            {t('qrPay.failed.refund', { total: usdt(payment.totalUsdt) })}
          </p>
        </div>
        <button
          onClick={pop}
          className="flex h-[56px] w-full items-center justify-center rounded-full border border-card bg-accent text-[15px] font-bold text-white active:bg-accent-ink"
        >
          {t('qrPay.done')}
        </button>
      </div>
    );
  }

  // ── Paying / settling ────────────────────────────────────────────────────
  if (step === 'paying' || step === 'settling') {
    return createPortal(
      <div className="fixed inset-0 z-[80] flex flex-col items-center justify-center gap-5 bg-black/[0.32] px-10 backdrop-blur-[8px]">
        <span className="size-12 animate-spin rounded-full border-[3px] border-card/30 border-t-white" />
        <p className="text-[16px] font-medium text-white">
          {step === 'paying' ? t('qrPay.processing') : t('qrPay.settling')}
        </p>
        {step === 'settling' && (
          <p className="max-w-[280px] text-center text-[13px] leading-[1.44] text-white/70">
            {t('qrPay.settlingHint')}
          </p>
        )}
      </div>,
      document.body,
    );
  }

  // ── Reauth ───────────────────────────────────────────────────────────────
  if (step === 'auth') {
    return (
      <ReauthGate
        onAuthed={runPay}
        bioTitle={t('qrPay.confirmTitle')}
        bioHint={t('security.sendUnlockHint')}
      />
    );
  }

  // ── The code named no price: the user does ───────────────────────────────
  if (step === 'amount' && store) {
    const typed = Number(amount);
    const ready = Number.isFinite(typed) && typed > 0;
    return (
      <div className="push-enter flex min-h-full flex-col bg-surface px-4 pb-safe pt-4">
        <div className="flex h-9 items-center justify-center">
          <h1 className="text-[16px] font-medium text-ink">{t('qrPay.amountTitle')}</h1>
        </div>

        <div className="flex flex-1 flex-col items-center justify-center gap-3">
          <span className="grid size-[56px] place-items-center rounded-[18px] bg-card shadow-card">
            <FigIcon name="pfReceipt" size={30} className="text-accent" />
          </span>
          {store.name && <p className="text-[17px] font-semibold text-ink">{store.name}</p>}
          <p className="-mt-1 text-[14px] text-muted">{t('qrPay.amountHint')}</p>

          <div className="mt-4 flex w-full items-center justify-center gap-2">
            <input
              autoFocus
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^\d.,]/g, '').replace(',', '.'))}
              placeholder="0"
              className="w-[60%] bg-transparent text-center text-[36px] font-semibold text-ink outline-none [font-family:'SF_Pro_Rounded',system-ui]"
            />
            <span className="text-[20px] font-medium text-muted">{store.currency}</span>
          </div>
        </div>

        <button
          disabled={!ready || busy}
          onClick={priceTypedAmount}
          className="flex h-[56px] w-full items-center justify-center rounded-full border border-card bg-accent text-[15px] font-bold text-white transition-opacity active:bg-accent-ink disabled:opacity-40"
        >
          {busy ? t('qrPay.scanning') : t('qrPay.amountCta')}
        </button>
      </div>
    );
  }

  // ── Confirm ──────────────────────────────────────────────────────────────
  if (step === 'estimate' && quote) {
    return (
      <div className="push-enter flex min-h-full flex-col bg-surface px-4 pb-safe pt-4">
        <div className="flex h-9 items-center justify-center">
          <h1 className="text-[16px] font-medium text-ink">{t('qrPay.confirmTitle')}</h1>
        </div>

        <div className="flex flex-1 flex-col justify-center">
          {/* merchant + total */}
          <div className="flex flex-col items-center gap-3">
            <span className="grid size-[56px] place-items-center rounded-[18px] bg-card shadow-card">
              <FigIcon name="pfReceipt" size={30} className="text-accent" />
            </span>
            <p className="text-[17px] font-semibold text-ink">
              {quote.merchantName ?? t('qrPay.theStore')}
            </p>
            <p className="-mt-1 text-[14px] text-muted">
              {quote.fiatAmount.toFixed(2)} {quote.fiatCurrency}
            </p>
            <div className="mt-2 flex items-center gap-2">
              <CoinIcon symbol="USDT" size={28} />
              <p className="[font-family:'SF_Pro_Rounded',system-ui] text-[32px] font-semibold text-ink">
                {usdt(quote.totalUsdt)}
              </p>
            </div>
          </div>

          {/* breakdown */}
          <div className="mt-8 flex flex-col rounded-[24px] bg-card px-4">
            <Row label={t('qrPay.amount')} value={usdt(quote.amountUsdt)} />
            <Row label={t('qrPay.antarcticFee')} value={usdt(quote.providerFeeUsdt)} />
            <Row label={t('qrPay.serviceFee')} value={usdt(quote.serviceFeeUsdt)} />
            <Row label={t('qrPay.total')} value={usdt(quote.totalUsdt)} strong last />
          </div>

          {short && (
            <p className="mt-4 text-center text-[13px] text-negative">
              {t('qrPay.err.insufficient')}
            </p>
          )}
        </div>

        <button
          disabled={short || !wallet}
          onClick={() => {
            haptic('medium');
            setStep('auth');
          }}
          className="mt-6 flex h-[56px] w-full items-center justify-center rounded-full border border-card bg-accent text-[15px] font-bold text-white transition-opacity active:bg-accent-ink disabled:opacity-40"
        >
          {t('qrPay.pay', { total: usdt(quote.totalUsdt) })}
        </button>
      </div>
    );
  }

  // ── Scan ─────────────────────────────────────────────────────────────────
  // min-h-full (not min-h-screen): Telegram's chrome already eats the viewport,
  // and 100vh pushes the CTA under the fold so the screen looks like a dead camera.
  return (
    <div className="slide-in-right flex min-h-full flex-col bg-surface px-4 pb-safe pt-4">
      <div className="flex h-9 shrink-0 items-center justify-center">
        <h1 className="text-[16px] font-medium text-ink">{t('qrPay.title')}</h1>
      </div>

      <div className="flex flex-1 flex-col items-center justify-center gap-8 py-6">
        {/* Decorative only — tap opens Telegram's native scanner. */}
        <button
          type="button"
          onClick={scan}
          disabled={busy}
          aria-label={t('qrPay.scanCta')}
          className="relative aspect-square w-[min(260px,70vw)] overflow-hidden rounded-[32px] bg-[#0e1116] active:opacity-90"
        >
          <span className="absolute inset-0 grid place-items-center">
            <FigIcon name="qrFill" size={96} className="text-white/10" />
          </span>
          <span className="qr-scanline absolute inset-x-6 top-1/2 h-[2px] rounded-full bg-accent shadow-[0_0_12px_2px_rgba(33,149,241,0.6)]" />
          {(
            [
              'left-4 top-4 border-l-[3px] border-t-[3px] rounded-tl-[14px]',
              'right-4 top-4 border-r-[3px] border-t-[3px] rounded-tr-[14px]',
              'left-4 bottom-4 border-l-[3px] border-b-[3px] rounded-bl-[14px]',
              'right-4 bottom-4 border-r-[3px] border-b-[3px] rounded-br-[14px]',
            ] as const
          ).map((c) => (
            <span key={c} className={`absolute size-8 border-card ${c}`} />
          ))}
        </button>

        <p className="max-w-[280px] text-center text-[15px] leading-[1.44] text-muted">
          {t('qrPay.scanHint')}
        </p>
      </div>

      {note && <p className="mb-3 shrink-0 text-center text-[13px] text-muted">{note}</p>}

      <button
        type="button"
        onClick={scan}
        disabled={busy}
        className="mb-6 flex h-[56px] w-full shrink-0 items-center justify-center gap-2 rounded-full border border-card bg-accent text-[15px] font-bold text-white transition-opacity active:bg-accent-ink disabled:opacity-60"
      >
        <FigIcon name="qr" size={20} className="text-white" />
        {busy ? t('qrPay.scanning') : t('qrPay.scanCta')}
      </button>
    </div>
  );
}

/** A breakdown row in the estimate card; `strong` bolds the total. */
function Row({
  label,
  value,
  strong,
  last,
}: {
  label: string;
  value: string;
  strong?: boolean;
  last?: boolean;
}) {
  return (
    <div
      className={`flex items-center justify-between gap-3 py-4 ${last ? '' : 'border-b border-hairline'}`}
    >
      <span className={`text-[15px] ${strong ? 'font-medium text-ink' : 'text-muted'}`}>
        {label}
      </span>
      <span className="text-[15px] font-medium text-ink">{value}</span>
    </div>
  );
}

/** Celebration screen — confetti, "Payment sent", Done. */
function SuccessView({
  total,
  merchant,
  onDone,
}: {
  total: string;
  merchant: string;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const pieces = useMemo(
    () =>
      Array.from({ length: 70 }, (_, i) => ({
        left: Math.random() * 100,
        delay: Math.random() * 1.2,
        dur: 2.4 + Math.random() * 1.8,
        size: 6 + Math.random() * 8,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length]!,
        rot: (Math.random() > 0.5 ? 1 : -1) * (540 + Math.round(Math.random() * 360)),
      })),
    [],
  );

  return createPortal(
    <div className="fixed inset-0 z-[80] flex flex-col bg-surface px-8 pb-8 pt-0">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {pieces.map((p, i) => (
          <span
            key={i}
            className="confetti-piece absolute rounded-[2px]"
            style={{
              left: `${p.left}%`,
              width: p.size,
              height: p.size * 0.45,
              backgroundColor: p.color,
              animationDelay: `${p.delay}s`,
              animationDuration: `${p.dur}s`,
              ['--rot' as never]: `${p.rot}deg`,
            }}
          />
        ))}
      </div>

      <div className="relative flex flex-1 flex-col items-center justify-center gap-5 text-center">
        <span className="grid size-[72px] place-items-center rounded-full bg-accent text-white">
          <FigIcon name="pfCheck" size={34} />
        </span>
        <h1 className="text-[24px] font-medium text-ink">{t('qrPay.sentTitle')}</h1>
        <p className="max-w-[280px] text-[15px] leading-[1.44] text-muted">
          {t('qrPay.sentDesc', { total, merchant })}
        </p>
      </div>

      <button
        onClick={onDone}
        className="relative flex h-[56px] w-full items-center justify-center rounded-full border border-card bg-accent text-[15px] font-bold text-white active:bg-accent-ink"
      >
        {t('qrPay.done')}
      </button>
    </div>,
    document.body,
  );
}
